import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useMediaQuery, useTheme } from "@mui/material";
import { ImpersonationState } from "@features/impersonation-feature/types";
import { useImpersonationState } from "../hooks/useImpersonationState";

interface UnifiedNavContextValue {
  isNavOpen: boolean;
  isDesktop: boolean;
  toggleNav: () => void;
  openNav: () => void;
  closeNav: () => void;
  impersonationState: ImpersonationState;
  setImpersonationState: React.Dispatch<
    React.SetStateAction<ImpersonationState>
  >;
  setImpersonationCollapse: (value: boolean) => void;
  cancelImpersonationCollapsePersistence: () => void;
}

const UnifiedNavContext = createContext<UnifiedNavContextValue | undefined>(
  undefined,
);

export function UnifiedNavProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"), {
    defaultMatches: true,
  });
  const [isNavOpen, setIsNavOpen] = useState(isDesktop);
  const {
    impersonationState,
    setImpersonationState,
    setImpersonationCollapse,
    cancelImpersonationCollapsePersistence,
  } = useImpersonationState();

  // When viewport size changes, auto-open on desktop and auto-close on mobile/tablet
  useEffect(() => {
    setIsNavOpen(isDesktop);
  }, [isDesktop]);

  const toggleNav = () => setIsNavOpen((prev) => !prev);
  const openNav = () => setIsNavOpen(true);
  const closeNav = () => setIsNavOpen(false);

  const contextValue = useMemo(
    () => ({
      isNavOpen,
      isDesktop,
      toggleNav,
      openNav,
      closeNav,
      impersonationState,
      setImpersonationState,
      setImpersonationCollapse,
      cancelImpersonationCollapsePersistence,
    }),
    [isNavOpen, isDesktop, impersonationState],
  );

  return (
    <UnifiedNavContext.Provider value={contextValue}>
      {children}
    </UnifiedNavContext.Provider>
  );
}

export function useUnifiedNav(): UnifiedNavContextValue {
  const ctx = useContext(UnifiedNavContext);
  if (!ctx) {
    throw new Error("useUnifiedNav must be used inside <UnifiedNavProvider>");
  }
  return ctx;
}
